import React, { createContext, useContext, useState, useEffect } from 'react';

interface OnboardingContextType {
    isFirstTimeUser: boolean;
    completeOnboarding: () => void;
    restartOnboarding: () => void;
    showTour: () => void;
    setNewUser: () => void;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

export const OnboardingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isFirstTimeUser, setIsFirstTimeUser] = useState(true);
    const [forceShowTour, setForceShowTour] = useState(false);

    useEffect(() => {
        // Check if user has completed onboarding before
        const hasCompletedOnboarding = localStorage.getItem('hasCompletedOnboarding');
        // Check if this is a newly registered user
        const isNewUser = localStorage.getItem('isNewUser');
        
        if (hasCompletedOnboarding === 'true' && !forceShowTour && isNewUser !== 'true') {
            setIsFirstTimeUser(false);
        } else if (isNewUser === 'true') {
            // Show the tour for new users
            setIsFirstTimeUser(true);
        }
    }, [forceShowTour]);

    // Clean up highlight elements when component unmounts
    useEffect(() => {
        return () => {
            document.querySelectorAll('.tour-highlight').forEach(el => {
                el.classList.remove('tour-highlight');
            });
        };
    }, []);

    const completeOnboarding = () => {
        // Clean up any highlights
        document.querySelectorAll('.tour-highlight').forEach(el => {
            el.classList.remove('tour-highlight');
        });
        
        localStorage.setItem('hasCompletedOnboarding', 'true');
        localStorage.removeItem('isNewUser'); // Remove new user flag after tour completes
        setIsFirstTimeUser(false);
        setForceShowTour(false);
    };

    const restartOnboarding = () => {
        // Clean up any highlights before starting anew
        document.querySelectorAll('.tour-highlight').forEach(el => {
            el.classList.remove('tour-highlight');
        });
        
        localStorage.setItem('hasCompletedOnboarding', 'false');
        setIsFirstTimeUser(true);
    };

    const showTour = () => {
        // Clean up any highlights before starting anew
        document.querySelectorAll('.tour-highlight').forEach(el => {
            el.classList.remove('tour-highlight');
        });
        
        setForceShowTour(true);
        setIsFirstTimeUser(true);
    };

    const setNewUser = () => {
        localStorage.setItem('isNewUser', 'true');
        localStorage.setItem('hasCompletedOnboarding', 'false');
        setIsFirstTimeUser(true);
    };

    return (
        <OnboardingContext.Provider value={{ 
            isFirstTimeUser: isFirstTimeUser || forceShowTour, 
            completeOnboarding, 
            restartOnboarding,
            showTour,
            setNewUser
        }}>
            {children}
        </OnboardingContext.Provider>
    );
};

export const useOnboarding = () => {
    const context = useContext(OnboardingContext);
    if (context === undefined) {
        throw new Error('useOnboarding must be used within an OnboardingProvider');
    }
    return context;
}; 